import { apiClient } from "./apiClient";

export interface SyncLegalEntityRequest {
  id: string;
  legalName?: string | null;
  primaryEmail?: string | null;
  parentId?: string | null;
}

export interface SyncBranchRequest {
  id: string;
  legalEntityId: string;
  name: string;
  email?: string | null;
  channelType: "WHATSAPP" | "EMAIL";
}

export interface SyncChannelRequest {
  channelType: "WHATSAPP";
  legalEntityId: string;
  branchId?: string | null;
  whatsappPhone: string;
  whatsappEnabled?: boolean | null;
  sourceCrm?: string | null;
  channelId: string;
  whatsappPhoneNumberId?: string | null;
  whatsappAccessToken?: string | null;
  whatsappBusinessAccountId?: string | null;
}

/** Atlas → chat-gateway /chatgateway/communication-channel body for EMAIL. */
export interface SyncEmailChannelRequest {
  channelType: "EMAIL";
  legalEntityId: string;
  branchId?: string | null;
  emailId: string;
  emailEnabled?: boolean | null;
  sourceCrm?: string | null;
  channelId: string;
  inboxLabel?: string | null;
  /**
   * branchId → branch name. For an LE-scoped channel this holds every branch
   * of the legal entity (incl. sub-entities); for a branch-scoped channel it
   * holds just that branch.
   */
  branches: Record<string, string>;
}

export interface FlowOnboardingRequest {
  legalEntityId: string;
  branchIds?: string[];
}

export interface SyncUsersRequest {
  /** Atlas legal entity UUID — all users for this LE will be synced. */
  legalEntityId: string;
  /** When set, scope the sync to users for this branch only. */
  branchId?: string | null;
}

/** Atlas → wa-gateway /api/ops/branches body. atlas-api attaches the Bearer. */
export interface WhatsappBranchRequest {
  name: string;
  legalEntityId: string;
  branchId?: string | null;
  channelId: string;
  whatsappPhoneNumber: string;
  whatsappPhoneNumberId: string;
  whatsappBusinessAccountId: string;
  whatsappAccessToken: string;
  active?: boolean | null;
}

/**
 * Atlas → wa-gateway /api/ops/routing-rules body. atlas-api resolves
 * {@code whatsappPhoneNumberId} to wa-gateway's internal branch id and
 * {@code environmentName} to its envId — both server-side concerns the UI
 * shouldn't be doing itself.
 */
export interface WhatsappRoutingRuleRequest {
  whatsappPhoneNumberId: string;
  environmentName: "pubsub-in" | "pubsub-out";
  direction: "INBOUND" | "OUTBOUND";
  senderPhoneNumber?: string;
  enabled?: boolean;
}

/**
 * Fan-out endpoints exposed by atlas-api at /api/ui/v1/chatgateway/*.
 * Atlas-api wraps these into the Pub/Sub envelope that bridge-chat-gateway
 * expects and forwards over plain REST.
 */
export const chatGatewaySyncService = {
  syncLegalEntity: (body: SyncLegalEntityRequest) =>
    apiClient.post("/chatgateway/legal-entity", body).then((r) => r.data),

  syncBranch: (body: SyncBranchRequest) =>
    apiClient.post("/chatgateway/branch", body).then((r) => r.data),

  syncChannel: (body: SyncChannelRequest) =>
    apiClient.post("/chatgateway/communication-channel", body).then((r) => r.data),

  syncEmailChannel: (body: SyncEmailChannelRequest) =>
    apiClient.post("/chatgateway/communication-channel", body).then((r) => r.data),

  syncUsers: (body: SyncUsersRequest) =>
    apiClient.post("/chatgateway/users", body).then((r) => r.data),

  flowOnboarding: (body: FlowOnboardingRequest) =>
    apiClient.post("/chatgateway/flow-onboarding", body).then((r) => r.data),

  whatsappBranch: (body: WhatsappBranchRequest) =>
    apiClient.post("/chatgateway/whatsapp/branch", body).then((r) => r.data),

  whatsappRoutingRule: (body: WhatsappRoutingRuleRequest) =>
    apiClient.post("/chatgateway/whatsapp/routing-rule", body).then((r) => r.data),
};
